import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Search, ChevronsLeft, ChevronsRight } from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * columns: [{ key, header, render?: (row)=>ReactNode, sortable?, className? }]
 * data: array
 * searchable: array of keys to search across (defaults to all string cells)
 * onRowClick: (row) => void
 */
export default function DataTable({
  columns,
  data,
  onRowClick,
  searchable,
  testIdPrefix = "dt",
  emptyMessage = "No records match your filters.",
  initialPageSize = 10,
}) {
  const [q, setQ] = useState("");
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    if (!q.trim()) return data;
    const query = q.toLowerCase();
    const keys = searchable || columns.map((c) => c.key);
    return data.filter((row) =>
      keys.some((k) => {
        const v = row[k];
        if (v == null) return false;
        return String(typeof v === "object" ? JSON.stringify(v) : v).toLowerCase().includes(query);
      })
    );
  }, [q, data, columns, searchable]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const end = Math.min(start + pageSize, filtered.length);
  const rows = filtered.slice(start, end);

  React.useEffect(() => { setPage(1); }, [q, pageSize, data]);

  const pageNumbers = useMemo(() => {
    const arr = [];
    const max = 5;
    let s = Math.max(1, currentPage - 2);
    let e = Math.min(totalPages, s + max - 1);
    s = Math.max(1, e - max + 1);
    for (let i = s; i <= e; i++) arr.push(i);
    return arr;
  }, [currentPage, totalPages]);

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-container`}>
      {/* Top controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Show</span>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger className="h-8 w-20" data-testid={`${testIdPrefix}-page-size`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 25, 50, 100].map((n) => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span>entries</span>
        </div>

        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
          <Input
            data-testid={`${testIdPrefix}-search`}
            placeholder="Search…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-8 h-8 w-56 text-xs"
          />
        </div>
      </div>

      {/* Table */}
      <div className="border border-border rounded-md overflow-hidden bg-card">
        <div className="overflow-auto max-h-[62vh]">
          <Table>
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow className="border-border hover:bg-transparent">
                {columns.map((c) => (
                  <TableHead key={c.key} className={cn("label-caps py-3", c.className)}>
                    {c.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow
                  key={row.id || row.event_id || row.code || i}
                  data-testid={`${testIdPrefix}-row-${row.id || row.event_id || row.code || i}`}
                  onClick={() => onRowClick && onRowClick(row)}
                  className={cn(
                    "border-border transition-colors",
                    onRowClick ? "cursor-pointer hover:bg-secondary/70" : ""
                  )}
                >
                  {columns.map((c) => (
                    <TableCell key={c.key} className={c.className}>
                      {c.render ? c.render(row) : row[c.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {rows.length === 0 && (
            <div className="py-14 text-center text-sm text-muted-foreground">{emptyMessage}</div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
        <span data-testid={`${testIdPrefix}-info`}>
          Showing <span className="text-foreground font-medium">{filtered.length === 0 ? 0 : start + 1}</span> to{" "}
          <span className="text-foreground font-medium">{end}</span> of{" "}
          <span className="text-foreground font-medium">{filtered.length}</span> entries
          {filtered.length !== data.length && <span className="text-muted-foreground"> (filtered from {data.length})</span>}
        </span>

        <div className="flex items-center gap-1">
          <PageBtn testId={`${testIdPrefix}-first`} disabled={currentPage === 1} onClick={() => setPage(1)}>
            <ChevronsLeft className="w-3.5 h-3.5" />
          </PageBtn>
          <PageBtn testId={`${testIdPrefix}-prev`} disabled={currentPage === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            <ChevronLeft className="w-3.5 h-3.5" />
          </PageBtn>
          {pageNumbers.map((n) => (
            <button
              key={n}
              data-testid={`${testIdPrefix}-page-${n}`}
              onClick={() => setPage(n)}
              className={cn(
                "h-7 min-w-7 px-2 text-xs rounded-sm border transition-colors",
                n === currentPage
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border hover:border-primary/50 hover:text-foreground"
              )}
            >
              {n}
            </button>
          ))}
          <PageBtn testId={`${testIdPrefix}-next`} disabled={currentPage === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            <ChevronRight className="w-3.5 h-3.5" />
          </PageBtn>
          <PageBtn testId={`${testIdPrefix}-last`} disabled={currentPage === totalPages} onClick={() => setPage(totalPages)}>
            <ChevronsRight className="w-3.5 h-3.5" />
          </PageBtn>
        </div>
      </div>
    </div>
  );
}

const PageBtn = ({ children, onClick, disabled, testId }) => (
  <Button
    variant="outline"
    size="icon"
    onClick={onClick}
    disabled={disabled}
    className="h-7 w-7"
    data-testid={testId}
  >
    {children}
  </Button>
);
